/**
 * SelectionService.ts
 * 
 * 职责：
 * 1. 管理绘图元素的选择状态和选择操作
 * 2. 提供选择框绘制和交互功能
 * 3. 处理选中元素的移动操作
 * 4. 计算和维护选择边界(bounds)信息
 * 5. 提供元素与选择区域的交叉检测算法
 * 6. 绘制选择框和选中内容的高亮显示
 * 7. 管理多选和单选模式
 * 8. 提供线段与矩形相交、线段与线段相交等几何计算功能
 * 9. 管理选中内容的删除操作
 * 10. 支持元素组合的选择和操作
 */

import { Bounds, Point, LineData, TextData, ShapeType, CardData, GroupData, GroupElement, ImageData } from '../../types';
import { ViewportService } from './ViewportService';
import { GroupService } from './GroupService';

export class SelectionService {
  private ctx: CanvasRenderingContext2D | null = null;
  private selectedIndices: number[] = [];
  private selectedTextIndices: number[] = [];
  private selectedCardIndices: number[] = [];
  private selectedImageIndices: number[] = [];
  private selectedGroupId: string | null = null;
  private selectionStart: Point | null = null;
  private selectionEnd: Point | null = null;
  private isMoving: boolean = false;
  private moveStart: Point | null = null;
  private lastMovePoint: Point | null = null;
  private selectedBounds: Bounds | null = null;
  private SELECTION_PADDING: number = 5; // 假设默认值为5
  private viewportService: ViewportService | null = null;
  private groupService: GroupService | null = null;
  // 添加记录激活控制点的变量
  private activeControlPoint: {position: 'TOP_LEFT' | 'TOP_RIGHT' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT' | 'TOP_CENTER' | 'RIGHT_CENTER' | 'BOTTOM_CENTER' | 'LEFT_CENTER' | null} = {
    position: null
  };
  
  constructor(viewportService?: ViewportService, groupService?: GroupService) {
    // 构造函数
    this.viewportService = viewportService || null;
    this.groupService = groupService || null;
  }

  // 设置画布上下文
  public setContext(ctx: CanvasRenderingContext2D): void {
    this.ctx = ctx;
  }

  // 获取选中的线条索引
  public getSelectedIndices(): number[] {
    return [...this.selectedIndices];
  }

  // 获取选中的文本索引
  public getSelectedTextIndices(): number[] {
    return [...this.selectedTextIndices];
  }

  // 获取选中的卡片索引
  public getSelectedCardIndices(): number[] {
    return [...this.selectedCardIndices];
  }

  // 获取选中的图片索引
  public getSelectedImageIndices(): number[] {
    return [...this.selectedImageIndices];
  }

  // 获取选中的组合ID
  public getSelectedGroupId(): string | undefined {
    return this.selectedGroupId || undefined;
  }

  // 设置选中的线条索引
  public setSelectedIndices(indices: number[]): void {
    this.selectedIndices = [...indices];
    // 重置选中边界，下次绘制时会重新计算
    this.selectedBounds = null;
  }

  // 设置选中的文本索引
  public setSelectedTextIndices(indices: number[]): void {
    this.selectedTextIndices = [...indices];
    // 重置选中边界，下次绘制时会重新计算
    this.selectedBounds = null;
  }

  // 设置选中的卡片索引
  public setSelectedCardIndices(indices: number[]): void {
    this.selectedCardIndices = [...indices];
    // 重置选中边界，下次绘制时会重新计算
    this.selectedBounds = null;
  }

  // 设置选中的图片索引
  public setSelectedImageIndices(indices: number[]): void {
    this.selectedImageIndices = [...indices];
    // 重置选中边界，下次绘制时会重新计算
    this.selectedBounds = null;
  }

  // 设置选中的组合ID
  public setSelectedGroupId(groupId: string | null): void {
    this.selectedGroupId = groupId;
  }

  // 清除所有选择
  public clearSelection(): void {
    this.selectedIndices = [];
    this.selectedTextIndices = [];
    this.selectedCardIndices = [];
    this.selectedImageIndices = [];
    this.selectedGroupId = null;
    this.selectionStart = null;
    this.selectionEnd = null;
    this.isMoving = false;
    this.moveStart = null;
    this.lastMovePoint = null;
    this.selectedBounds = null;
  }
  
  // 仅清除选中边界缓存，保留选中状态
  public clearSelectedBounds(): void {
    this.selectedBounds = null;
  }

  // 开始选择区域
  public startSelection(point: Point): void {
    this.selectionStart = point;
    this.selectionEnd = point;
  }

  // 更新选择区域
  public updateSelection(point: Point): void {
    if (!this.selectionStart) {
      this.selectionStart = point;
    }
    this.selectionEnd = point;
    
    // 重置选中边界缓存，以便在预览高亮元素时能正确计算边界
    this.selectedBounds = null;
  }

  // 完成选择，并根据选区确定选中的线条
  public finishSelection(addToExisting: boolean = false): void {
    if (!this.selectionStart || !this.selectionEnd) {
      return;
    }
    
    // 重置选择框，但不重置选中的线条索引，因为这些索引在completeLineSelection中处理
    this.selectionStart = null;
    this.selectionEnd = null;
    // 重置选中边界，下次绘制时会重新计算
    this.selectedBounds = null;
  }

  // 获取选择框边界
  public getSelectionBounds(): Bounds {
    if (!this.selectionStart || !this.selectionEnd) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    }
    
    const minX = Math.min(this.selectionStart.x, this.selectionEnd.x);
    const minY = Math.min(this.selectionStart.y, this.selectionEnd.y);
    const maxX = Math.max(this.selectionStart.x, this.selectionEnd.x);
    const maxY = Math.max(this.selectionStart.y, this.selectionEnd.y);
    
    return { minX, minY, maxX, maxY };
  }

  // 获取选中内容的边界
  public getSelectedContentBounds(lines: LineData[], texts: TextData[] = [], cards: CardData[] = [], images: ImageData[] = []): Bounds {
    // 没有选中内容，返回空白边界
    if (this.selectedIndices.length === 0 && 
        this.selectedTextIndices.length === 0 && 
        this.selectedCardIndices.length === 0 &&
        this.selectedImageIndices.length === 0) {
      return {
        minX: 0,
        minY: 0,
        maxX: 0,
        maxY: 0
      };
    }
    
    // 如果已经计算过边界，直接返回
    if (this.selectedBounds) {
      return this.selectedBounds;
    }
    
    // 初始边界为最大可能范围
    let bounds: Bounds = {
      minX: Number.MAX_VALUE,
      minY: Number.MAX_VALUE,
      maxX: -Number.MAX_VALUE,
      maxY: -Number.MAX_VALUE
    };
    
    // 计算选中线条的边界
    for (const index of this.selectedIndices) {
      if (index < 0 || index >= lines.length) continue;
      
      const line = lines[index];
      const lineBounds = this.getLineBoundingBox(line, false); // 不包含边距
      
      // 更新整体边界
      bounds.minX = Math.min(bounds.minX, lineBounds.minX);
      bounds.minY = Math.min(bounds.minY, lineBounds.minY);
      bounds.maxX = Math.max(bounds.maxX, lineBounds.maxX);
      bounds.maxY = Math.max(bounds.maxY, lineBounds.maxY);
    }
    
    // 计算选中文本的边界
    for (const index of this.selectedTextIndices) {
      if (index < 0 || index >= texts.length) continue;
      
      const text = texts[index];
      const textBounds = this.getTextBoundingBox(text, false); // 不包含边距
      
      // 更新整体边界
      bounds.minX = Math.min(bounds.minX, textBounds.minX);
      bounds.minY = Math.min(bounds.minY, textBounds.minY);
      bounds.maxX = Math.max(bounds.maxX, textBounds.maxX);
      bounds.maxY = Math.max(bounds.maxY, textBounds.maxY);
    }
    
    // 计算选中卡片的边界
    for (const index of this.selectedCardIndices) {
      if (index < 0 || index >= cards.length) continue;
      
      const card = cards[index];
      const cardBounds = this.getCardBoundingBox(card, false); // 不包含边距
      
      // 更新整体边界
      bounds.minX = Math.min(bounds.minX, cardBounds.minX);
      bounds.minY = Math.min(bounds.minY, cardBounds.minY);
      bounds.maxX = Math.max(bounds.maxX, cardBounds.maxX);
      bounds.maxY = Math.max(bounds.maxY, cardBounds.maxY);
    }
    
    // 计算选中图片的边界
    for (const index of this.selectedImageIndices) {
      if (index < 0 || index >= images.length) continue;
      
      const image = images[index];
      // 使用无边距的边界
      const imageBounds = this.getImageBoundingBox(image, false);
      
      // 更新整体边界
      bounds.minX = Math.min(bounds.minX, imageBounds.minX);
      bounds.minY = Math.min(bounds.minY, imageBounds.minY);
      bounds.maxX = Math.max(bounds.maxX, imageBounds.maxX);
      bounds.maxY = Math.max(bounds.maxY, imageBounds.maxY);
    }
    
    // 缓存计算结果以便下次使用
    this.selectedBounds = bounds;
    
    return bounds;
  }

  // 检查点是否在选中内容的边界内
  public isPointInSelectionBounds(point: Point, lines: LineData[] = [], texts: TextData[] = [], cards: CardData[] = [], images: ImageData[] = []): boolean {
    if (!this.selectedIndices.length && !this.selectedTextIndices.length && !this.selectedCardIndices.length && !this.selectedImageIndices.length) {
      return false;
    }
    
    // 获取当前缩放比例，用于调整检测精度
    const currentScale = this.viewportService ? this.viewportService.getScale() : 1.0;
    const padding = 5 / currentScale; // 与绘制边框时使用的内边距相同
    
    // 检测是否在任何选中的线条边界内
    for (const index of this.selectedIndices) {
      if (index < lines.length) {
        const line = lines[index];
        const lineBounds = this.getLineBoundingBox(line);
        
        // 检查点是否在线条边框内
        if (this.pointInBounds(point, lineBounds)) {
          return true;
        }
      }
    }
    
    // 检测是否在任何选中的文本边界内
    for (const index of this.selectedTextIndices) {
      if (index < texts.length) {
        const text = texts[index];
        const textBounds = this.getTextBoundingBox(text);
        
        // 检查点是否在文本边框内
        if (this.pointInBounds(point, textBounds)) {
          return true;
        }
      }
    }
    
    // 检测是否在任何选中的卡片边界内
    for (const index of this.selectedCardIndices) {
      if (index < cards.length) {
        const card = cards[index];
        const cardBounds = this.getCardBoundingBox(card);
        
        // 检查点是否在卡片边框内
        if (this.pointInBounds(point, cardBounds)) {
          return true;
        }
      }
    }
    
    // 检测是否在任何选中的图片边界内
    for (const index of this.selectedImageIndices) {
      if (index < images.length) {
        const image = images[index];
        // 使用无边距的边界进行检测
        const imageBounds = this.getImageBoundingBox(image, false);
        
        // 检查点是否在图片边框内
        if (this.pointInBounds(point, imageBounds)) {
          return true;
        }
      }
    }
    
    // 如果有多个对象被选中或选中了组合，还需要检查点是否在整体选择框内
    const totalSelected = this.selectedIndices.length + this.selectedTextIndices.length + 
                        this.selectedCardIndices.length + this.selectedImageIndices.length;
    if (totalSelected > 1 || this.selectedGroupId) {
      const bounds = this.getSelectedContentBounds(lines, texts, cards, images);
      
      // 添加边距
      const boundsWithPadding = {
        minX: bounds.minX - padding,
        minY: bounds.minY - padding,
        maxX: bounds.maxX + padding,
        maxY: bounds.maxY + padding
      };
      
      return this.pointInBounds(point, boundsWithPadding);
    }
    
    return false;
  }

  // 开始移动选中内容
  public startMoving(point: Point): void {
    this.isMoving = true;
    this.moveStart = point;
    this.lastMovePoint = point;
  }

  // 移动选中内容
  public moveSelectedContent(point: Point): void {
    if (!this.isMoving || !this.lastMovePoint) {
      return;
    }
    
    this.lastMovePoint = point;
    // 移动时重置选中边界
    this.selectedBounds = null;
  }

  // 停止移动选中内容
  public stopMoving(): void {
    this.isMoving = false;
    this.moveStart = null;
    this.lastMovePoint = null;
    // 停止移动时重置选中边界
    this.selectedBounds = null;
  }

  // 判断是否正在移动内容
  public isContentMoving(): boolean {
    return this.isMoving;
  }

  // 判断是否正在进行区域选择
  public isSelecting(): boolean {
    return this.selectionStart !== null && this.selectionEnd !== null;
  }

  // 判断文本是否在选择框内
  public isTextInSelectionBounds(text: TextData, selectionBounds: Bounds): boolean {
    if (!text.text.trim()) return false; // 忽略空文本
    
    // 获取文本的边界框
    const textBounds = this.getTextBoundingBox(text);
    
    // 检查两个边界框是否相交
    return this.boundsIntersect(textBounds, selectionBounds);
  }

  // 完成选择操作
  public completeSelection(
    lines: LineData[], 
    addToExisting: boolean = false, 
    texts: TextData[] = [], 
    cards: CardData[] = [], 
    images: ImageData[] = []
  ): void {
    if (!this.selectionStart || !this.selectionEnd) {
      return;
    }
    
    // 获取选择框的边界
    const bounds = this.getSelectionBounds();
    
    // 如果不是添加到现有选择，则先清除现有的选择
    if (!addToExisting) {
      this.selectedIndices = [];
      this.selectedTextIndices = [];
      this.selectedCardIndices = [];
      this.selectedImageIndices = [];
      this.selectedGroupId = null;
    }
    
    // 记录找到的组ID，避免重复处理
    const groupIds = new Set<string>();
    
    // 寻找在选择区域内的线条
    lines.forEach((line, index) => {
      // 已经选中的不重复选择
      if (this.selectedIndices.includes(index)) {
        return;
      }
      
      // 根据线条类型检查是否在选择区域内
      if (this.isLineInSelectionBounds(line, bounds)) {
        // 检查这个线条是否属于某个组
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('line', index);
          if (groupId) {
            groupIds.add(groupId);
            return; // 不单独添加，稍后会添加整个组
          }
        }
        
        this.selectedIndices.push(index);
      }
    });
    
    // 寻找在选择区域内的文本
    texts.forEach((text, index) => {
      // 已经选中的不重复选择
      if (this.selectedTextIndices.includes(index)) {
        return;
      }
      
      // 检查文本是否在选择区域内
      if (this.isTextInSelectionBounds(text, bounds)) {
        // 检查这个文本是否属于某个组
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('text', index);
          if (groupId) {
            groupIds.add(groupId);
            return; // 不单独添加，稍后会添加整个组
          }
        }
        
        this.selectedTextIndices.push(index);
      }
    });
    
    // 寻找在选择区域内的卡片
    cards.forEach((card, index) => {
      // 已经选中的不重复选择
      if (this.selectedCardIndices.includes(index)) {
        return;
      }
      
      // 检查卡片是否在选择区域内
      if (this.isCardInSelectionBounds(card, bounds)) {
        // 检查这个卡片是否属于某个组
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('card', index);
          if (groupId) {
            groupIds.add(groupId);
            return; // 不单独添加，稍后会添加整个组
          }
        }
        
        this.selectedCardIndices.push(index);
      }
    });
    
    // 寻找在选择区域内的图片
    images.forEach((image, index) => {
      // 已经选中的不重复选择
      if (this.selectedImageIndices.includes(index)) {
        return;
      }
      
      // 检查图片是否在选择区域内
      if (this.isImageInSelectionBounds(image, bounds)) {
        // 检查这个图片是否属于某个组
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('image', index);
          if (groupId) {
            groupIds.add(groupId);
            return; // 不单独添加，稍后会添加整个组
          }
        }
        
        this.selectedImageIndices.push(index);
      }
    });
    
    // 如果找到了组，选中这些组中的所有元素
    if (groupIds.size > 0) {
      // 如果只选中了一个组，设置selectedGroupId
      if (groupIds.size === 1) {
        this.selectedGroupId = Array.from(groupIds)[0];
      }
      
      // 添加所有组中的元素到选择
      groupIds.forEach(groupId => {
        if (this.groupService) {
          const groupElements = this.groupService.getGroupElements(groupId);
          for (const element of groupElements) {
            if (element.type === 'line' && !this.selectedIndices.includes(element.index)) {
              this.selectedIndices.push(element.index);
            } else if (element.type === 'text' && !this.selectedTextIndices.includes(element.index)) {
              this.selectedTextIndices.push(element.index);
            } else if (element.type === 'card' && !this.selectedCardIndices.includes(element.index)) {
              this.selectedCardIndices.push(element.index);
            } else if (element.type === 'image' && !this.selectedImageIndices.includes(element.index)) {
              this.selectedImageIndices.push(element.index);
            }
          }
        }
      });
    }
    
    // 重置选择框状态
    this.selectionStart = null;
    this.selectionEnd = null;
    
    // 重置选中内容边界，在下次需要时重新计算
    this.selectedBounds = null;
  }

  // 绘制选中内容的边框
  public drawSelectedContentBounds(lines: LineData[], texts: TextData[] = [], cards: CardData[] = [], images: ImageData[] = []): void {
    if (!this.ctx) return;
    
    // 如果没有选中任何内容，直接返回
    if (this.selectedIndices.length === 0 && 
        this.selectedTextIndices.length === 0 && 
        this.selectedCardIndices.length === 0 &&
        this.selectedImageIndices.length === 0) {
      return;
    }
    
    // 计算选中元素的总数
    const totalSelectedElements = 
      this.selectedIndices.length + 
      this.selectedTextIndices.length + 
      this.selectedCardIndices.length +
      this.selectedImageIndices.length;
    
    // 只有当选中多个元素时，才绘制蓝色边框
    if (totalSelectedElements > 1) {
      // 重新计算选中元素的边界
      const bounds = this.getSelectedContentBounds(lines, texts, cards, images);
      if (!bounds || (bounds.minX === Infinity && bounds.minY === Infinity)) {
        return; // 无效边界
      }
      
      // 保存当前上下文
      this.ctx.save();
      
      // 获取视口缩放比例
      const currentScale = this.viewportService ? this.viewportService.getScale() : 1.0;
      
      // 根据缩放比例设置线宽和填充样式
      const lineWidth = 1.5 / currentScale;
      
      // 使用固定的蓝色边框样式，不管是否按住Shift选择
      this.ctx.lineWidth = lineWidth;
      this.ctx.strokeStyle = 'rgba(0, 120, 215, 0.8)';
      
      // 添加内边距
      const padding = this.SELECTION_PADDING / currentScale;
      
      // 绘制实线选择框（不再使用虚线）
      this.ctx.beginPath();
      this.ctx.setLineDash([]); // 清除虚线样式，使用实线
      this.ctx.strokeRect(
        bounds.minX - padding,
        bounds.minY - padding,
        bounds.maxX - bounds.minX + (padding * 2),
        bounds.maxY - bounds.minY + (padding * 2)
      );
      
      // 恢复上下文
      this.ctx.restore();
    }
    
    // 为每个选中的元素绘制边框
    this.drawSelectedBorders(lines, texts, cards, images);
  }

  // 绘制各个元素的高亮状态
  private drawIndividualElementHighlights(lines: LineData[], texts: TextData[] = []): void {
    if (!this.ctx) return;
    
    this.ctx.save();
    
    // 获取当前缩放比例
    const currentScale = this.viewportService ? this.viewportService.getScale() : 1.0;
    
    // 高亮选中的文本
    this.selectedTextIndices.forEach(index => {
      if (index < texts.length) {
        const text = texts[index];
        
        // 使用getTextBoundingBox获取准确的文本边界
        const textBounds = this.getTextBoundingBox(text, false); // 不包含边距
        
        // 为文本绘制半透明蓝色背景 - 根据缩放比例调整不透明度
        this.ctx!.fillStyle = `rgba(0, 120, 215, ${Math.max(0.2, 0.1 + (0.3 / currentScale))})`;
        
        // 增加外边距，根据缩放比例调整
        const padding = 2 / currentScale;
        this.ctx!.fillRect(
          textBounds.minX - padding, 
          textBounds.minY - padding, 
          textBounds.maxX - textBounds.minX + (padding * 2), 
          textBounds.maxY - textBounds.minY + (padding * 2)
        );
      }
    });
    
    this.ctx.restore();
  }

  // 预览选择
  public previewSelection(lines: LineData[], texts: TextData[] = [], cards: CardData[] = [], images: ImageData[] = []): void {
    if (!this.ctx || !this.selectionStart || !this.selectionEnd) {
      return;
    }
    
    // 获取当前缩放
    const currentScale = this.viewportService ? this.viewportService.getScale() : 1.0;
    
    // 绘制选择框
    this.ctx.save();
    this.ctx.lineWidth = 1 / currentScale;
    this.ctx.strokeStyle = 'rgba(0, 120, 215, 0.8)';
    this.ctx.fillStyle = 'rgba(0, 120, 215, 0.1)';
    
    // 计算选择区域
    const selectionBounds = this.getSelectionBounds();
    
    // 绘制矩形选择框
    this.ctx.beginPath();
    this.ctx.rect(
      selectionBounds.minX, 
      selectionBounds.minY, 
      selectionBounds.maxX - selectionBounds.minX, 
      selectionBounds.maxY - selectionBounds.minY
    );
    this.ctx.fill();
    this.ctx.stroke();
    
    // 高亮预览选择中的元素
    this.previewHighlightElements(lines, texts, cards, images, selectionBounds);
    
    // 恢复上下文
    this.ctx.restore();
  }

  // 预览高亮元素
  private previewHighlightElements(lines: LineData[], texts: TextData[], cards: CardData[], images: ImageData[] = [], selectionBounds: Bounds): void {
    if (!this.ctx) return;
    
    // 保存当前选择状态
    const savedIndices = [...this.selectedIndices];
    const savedTextIndices = [...this.selectedTextIndices];
    const savedCardIndices = [...this.selectedCardIndices];
    const savedImageIndices = [...this.selectedImageIndices];
    const savedGroupId = this.selectedGroupId;
    
    // 临时清除当前选择（不使用clearSelection方法，因为它会重置其他状态）
    this.selectedIndices = [];
    this.selectedTextIndices = [];
    this.selectedCardIndices = [];
    this.selectedImageIndices = [];
    this.selectedGroupId = null;
    
    // 确保边界缓存被清除，强制重新计算
    this.selectedBounds = null;
    
    // 临时选择在预览区域内的元素
    const groupsFound = new Set<string>();
    
    // 检查框选区域内的线条，如果属于组则选择整个组
    lines.forEach((line, index) => {
      if (!this.selectedIndices.includes(index) && this.isLineInSelectionBounds(line, selectionBounds)) {
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('line', index);
          if (groupId && !groupsFound.has(groupId)) {
            // 添加组中的所有元素
            const elements = this.groupService.getGroupElements(groupId);
            for (const element of elements) {
              if (element.type === 'line' && !this.selectedIndices.includes(element.index)) {
                this.selectedIndices.push(element.index);
              } else if (element.type === 'text' && !this.selectedTextIndices.includes(element.index)) {
                this.selectedTextIndices.push(element.index);
              } else if (element.type === 'card' && !this.selectedCardIndices.includes(element.index)) {
                this.selectedCardIndices.push(element.index);
              } else if (element.type === 'image' && !this.selectedImageIndices.includes(element.index)) {
                this.selectedImageIndices.push(element.index);
              }
            }
            groupsFound.add(groupId);
          } else if (!groupId) {
            // 不属于任何组，直接添加
            this.selectedIndices.push(index);
          }
        } else {
          // 不存在组服务，直接添加
          this.selectedIndices.push(index);
        }
      }
    });
    
    // 检查框选区域内的文本
    texts.forEach((text, index) => {
      if (!this.selectedTextIndices.includes(index) && this.isTextInSelectionBounds(text, selectionBounds)) {
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('text', index);
          if (groupId && !groupsFound.has(groupId)) {
            // 添加组中的所有元素
            const elements = this.groupService.getGroupElements(groupId);
            for (const element of elements) {
              if (element.type === 'line' && !this.selectedIndices.includes(element.index)) {
                this.selectedIndices.push(element.index);
              } else if (element.type === 'text' && !this.selectedTextIndices.includes(element.index)) {
                this.selectedTextIndices.push(element.index);
              } else if (element.type === 'card' && !this.selectedCardIndices.includes(element.index)) {
                this.selectedCardIndices.push(element.index);
              } else if (element.type === 'image' && !this.selectedImageIndices.includes(element.index)) {
                this.selectedImageIndices.push(element.index);
              }
            }
            groupsFound.add(groupId);
          } else if (!groupId) {
            // 不属于任何组，直接添加
            this.selectedTextIndices.push(index);
          }
        } else {
          // 不存在组服务，直接添加
          this.selectedTextIndices.push(index);
        }
      }
    });
    
    // 检查框选区域内的卡片
    cards.forEach((card, index) => {
      if (!this.selectedCardIndices.includes(index) && this.isCardInSelectionBounds(card, selectionBounds)) {
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('card', index);
          if (groupId && !groupsFound.has(groupId)) {
            // 添加组中的所有元素
            const elements = this.groupService.getGroupElements(groupId);
            for (const element of elements) {
              if (element.type === 'line' && !this.selectedIndices.includes(element.index)) {
                this.selectedIndices.push(element.index);
              } else if (element.type === 'text' && !this.selectedTextIndices.includes(element.index)) {
                this.selectedTextIndices.push(element.index);
              } else if (element.type === 'card' && !this.selectedCardIndices.includes(element.index)) {
                this.selectedCardIndices.push(element.index);
              } else if (element.type === 'image' && !this.selectedImageIndices.includes(element.index)) {
                this.selectedImageIndices.push(element.index);
              }
            }
            groupsFound.add(groupId);
          } else if (!groupId) {
            // 不属于任何组，直接添加
            this.selectedCardIndices.push(index);
          }
        } else {
          // 不存在组服务，直接添加
          this.selectedCardIndices.push(index);
        }
      }
    });
    
    // 检查框选区域内的图片
    images.forEach((image, index) => {
      if (!this.selectedImageIndices.includes(index) && this.isImageInSelectionBounds(image, selectionBounds)) {
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('image', index);
          if (groupId && !groupsFound.has(groupId)) {
            // 添加组中的所有元素
            const elements = this.groupService.getGroupElements(groupId);
            for (const element of elements) {
              if (element.type === 'line' && !this.selectedIndices.includes(element.index)) {
                this.selectedIndices.push(element.index);
              } else if (element.type === 'text' && !this.selectedTextIndices.includes(element.index)) {
                this.selectedTextIndices.push(element.index);
              } else if (element.type === 'card' && !this.selectedCardIndices.includes(element.index)) {
                this.selectedCardIndices.push(element.index);
              } else if (element.type === 'image' && !this.selectedImageIndices.includes(element.index)) {
                this.selectedImageIndices.push(element.index);
              }
            }
            groupsFound.add(groupId);
          } else if (!groupId) {
            // 不属于任何组，直接添加
            this.selectedImageIndices.push(index);
          }
        } else {
          // 不存在组服务，直接添加
          this.selectedImageIndices.push(index);
        }
      }
    });
    
    // 如果只有一个组被选中，设置selectedGroupId
    if (groupsFound.size === 1) {
      this.selectedGroupId = Array.from(groupsFound)[0];
    }
    
    // 使用现有方法绘制选中内容的边界
    this.drawSelectedContentBounds(lines, texts, cards, images);
    
    // 恢复原始选择状态
    this.selectedIndices = savedIndices;
    this.selectedTextIndices = savedTextIndices;
    this.selectedCardIndices = savedCardIndices;
    this.selectedImageIndices = savedImageIndices;
    this.selectedGroupId = savedGroupId;
  }

  // 判断线条是否与选择框相交
  public isLineInSelectionBounds(line: LineData, selectionBounds: Bounds): boolean {
    if (line.points.length === 0) return false; // 忽略无点线条
    
    // 获取线条的边界框
    const lineBounds = this.getLineBoundingBox(line);
    
    // 先用边界框快速检测
    if (!this.boundsIntersect(lineBounds, selectionBounds)) {
      return false; // 边界框不相交，肯定不在选择框内
    }
    
    // 进一步检查是否有点在选择框内
    const anyPointInSelection = line.points.some(point => 
      this.pointInBounds(point, selectionBounds)
    );
    
    if (anyPointInSelection) {
      return true;
    }
    
    // 如果没有点在选择框内，检查线段是否与选择框相交
    if (line.points.length > 1) {
      for (let i = 1; i < line.points.length; i++) {
        const p1 = line.points[i-1];
        const p2 = line.points[i];
        
        if (this.isLineSegmentIntersectingRect(p1, p2, selectionBounds)) {
          return true;
        }
      }
    }
    
    return false;
  }

  // 修改判断形状是否与选择框相交的方法 - 使用与线条相同的判定逻辑
  public isShapeInSelectionBounds(shape: {points?: Point[], startPoint?: Point, endPoint?: Point}, bounds: Bounds): boolean {
    // 确保形状有点集合
    if (!shape.points || shape.points.length <= 1) {
      // 如果没有点集合或点数不足，退回到使用起点和终点
      if (shape.startPoint && shape.endPoint) {
        return this.isLineSegmentIntersectingRect(shape.startPoint, shape.endPoint, bounds);
      }
      return false;
    }
    
    // 与线条使用相同的逻辑：检查任何点是否在选区内
    const isAnyPointInSelection = shape.points.some((point: Point) => 
      point.x >= bounds.minX && 
      point.x <= bounds.maxX && 
      point.y >= bounds.minY && 
      point.y <= bounds.maxY
    );
    
    if (isAnyPointInSelection) return true;
    
    // 检查形状的线段是否与选区边界相交
    for (let i = 1; i < shape.points.length; i++) {
      const p1 = shape.points[i-1];
      const p2 = shape.points[i];
      
      if (this.isLineSegmentIntersectingRect(p1, p2, bounds)) {
        return true;
      }
    }
    
    return false;
  }

  private isLineSegmentIntersectingRect(
    p1: Point, 
    p2: Point, 
    rect: Bounds
  ): boolean {
    // 检查两个端点是否都在矩形内
    if (
      (p1.x >= rect.minX && p1.x <= rect.maxX && p1.y >= rect.minY && p1.y <= rect.maxY) ||
      (p2.x >= rect.minX && p2.x <= rect.maxX && p2.y >= rect.minY && p2.y <= rect.maxY)
    ) {
      return true;
    }
    
    // 检查线段是否与矩形的四条边相交
    const rectLines = [
      {p1: {x: rect.minX, y: rect.minY}, p2: {x: rect.maxX, y: rect.minY}}, // 上边
      {p1: {x: rect.maxX, y: rect.minY}, p2: {x: rect.maxX, y: rect.maxY}}, // 右边
      {p1: {x: rect.maxX, y: rect.maxY}, p2: {x: rect.minX, y: rect.maxY}}, // 下边
      {p1: {x: rect.minX, y: rect.maxY}, p2: {x: rect.minX, y: rect.minY}}  // 左边
    ];
    
    // 检查线段与矩形的每一条边是否相交
    for (const rectLine of rectLines) {
      if (this.doLineSegmentsIntersect(p1, p2, rectLine.p1, rectLine.p2)) {
        return true;
      }
    }
    
    return false;
  }
  
  // 检查两条线段是否相交
  private doLineSegmentsIntersect(
    p1: Point, 
    p2: Point, 
    p3: Point, 
    p4: Point
  ): boolean {
    // 计算方向
    const d1 = this.direction(p3, p4, p1);
    const d2 = this.direction(p3, p4, p2);
    const d3 = this.direction(p1, p2, p3);
    const d4 = this.direction(p1, p2, p4);
    
    // 如果方向不同且都不为0，则线段相交
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && 
        ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
      return true;
    }
    
    // 如果线段的端点在另一条线段上
    if (d1 === 0 && this.isPointOnLineSegment(p3, p4, p1)) return true;
    if (d2 === 0 && this.isPointOnLineSegment(p3, p4, p2)) return true;
    if (d3 === 0 && this.isPointOnLineSegment(p1, p2, p3)) return true;
    if (d4 === 0 && this.isPointOnLineSegment(p1, p2, p4)) return true;
    
    return false;
  }
  
  // 计算方向
  private direction(p1: Point, p2: Point, p3: Point): number {
    return (p3.x - p1.x) * (p2.y - p1.y) - (p2.x - p1.x) * (p3.y - p1.y);
  }
  
  // 检查点是否在线段上
  private isPointOnLineSegment(p1: Point, p2: Point, p: Point): boolean {
    return p.x <= Math.max(p1.x, p2.x) && p.x >= Math.min(p1.x, p2.x) &&
           p.y <= Math.max(p1.y, p2.y) && p.y >= Math.min(p1.y, p2.y);
  }

  // 删除选中的内容
  public deleteSelectedContent(drawingService: any, textService: any, cardService: any, imageService?: any): void {
    // 在删除线条之前，先通知GroupService
    if (this.groupService && this.selectedIndices.length > 0) {
      // 从索引大的开始处理，避免索引变化影响结果
      const sortedIndices = [...this.selectedIndices].sort((a, b) => b - a);
      for (const index of sortedIndices) {
        this.groupService.removeElementFromGroups('line', index);
      }
    }
    
    // 将选中的线条索引传递给绘图服务进行删除
    if (this.selectedIndices.length > 0) {
      drawingService.deleteLines(this.selectedIndices);
    }
    
    // 文本和卡片的删除也需要类似处理
    if (this.groupService && this.selectedTextIndices.length > 0) {
      const sortedIndices = [...this.selectedTextIndices].sort((a, b) => b - a);
      for (const index of sortedIndices) {
        this.groupService.removeElementFromGroups('text', index);
      }
    }
    
    // 将选中的文本索引传递给文本服务进行删除
    if (this.selectedTextIndices.length > 0) {
      textService.deleteTexts(this.selectedTextIndices);
    }
    
    // 处理卡片
    if (this.groupService && this.selectedCardIndices.length > 0) {
      const sortedIndices = [...this.selectedCardIndices].sort((a, b) => b - a);
      for (const index of sortedIndices) {
        this.groupService.removeElementFromGroups('card', index);
      }
    }
    
    // 将选中的卡片索引传递给卡片服务进行删除
    if (this.selectedCardIndices.length > 0) {
      cardService.deleteCards(this.selectedCardIndices);
    }
    
    // 处理图片
    if (imageService && this.groupService && this.selectedImageIndices.length > 0) {
      const sortedIndices = [...this.selectedImageIndices].sort((a, b) => b - a);
      for (const index of sortedIndices) {
        this.groupService.removeElementFromGroups('image', index);
      }
      
      // 将选中的图片索引传递给图片服务进行删除
      if (this.selectedImageIndices.length > 0) {
        imageService.deleteImages(this.selectedImageIndices);
      }
    }
    
    // 清除选择
    this.clearSelection();
    
    // 触发工具操作完成事件，自动保存当前内容状态
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
  }

  private calculateLineBounds(line: LineData): Bounds {
    const points = line.points;
    
    // 如果线条没有点或者只有一个点，返回一个包围该点的边界
    if (!points.length) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    } else if (points.length === 1) {
      const p = points[0];
      return {
        minX: p.x - line.size - this.SELECTION_PADDING,
        minY: p.y - line.size - this.SELECTION_PADDING,
        maxX: p.x + line.size + this.SELECTION_PADDING,
        maxY: p.y + line.size + this.SELECTION_PADDING
      };
    }
    
    // 初始化边界值
    let minX = points[0].x;
    let minY = points[0].y;
    let maxX = points[0].x;
    let maxY = points[0].y;
    
    // 找出所有点中的最大和最小坐标
    for (let i = 1; i < points.length; i++) {
      minX = Math.min(minX, points[i].x);
      minY = Math.min(minY, points[i].y);
      maxX = Math.max(maxX, points[i].x);
      maxY = Math.max(maxY, points[i].y);
    }
    
    // 添加线条大小和一些填充作为边界
    minX -= line.size + this.SELECTION_PADDING;
    minY -= line.size + this.SELECTION_PADDING;
    maxX += line.size + this.SELECTION_PADDING;
    maxY += line.size + this.SELECTION_PADDING;
    
    return { minX, minY, maxX, maxY };
  }

  // 拷贝选中的线条
  public copySelectedLines(lines: LineData[]): LineData[] {
    const selectedIndices = this.selectedIndices;
    const copiedLines: LineData[] = [];
    
    selectedIndices.forEach(index => {
      if (index < lines.length) {
        const original = lines[index];
        
        // 拷贝线条的所有属性
        const copiedLine: LineData = {
          path: new Path2D(),
          color: original.color,
          size: original.size,
          points: original.points.map(p => ({ x: p.x, y: p.y })), // 深拷贝点数组
          isConvertedShape: false,
          originalShapeType: undefined,
          shapeStartPoint: undefined,
          shapeEndPoint: undefined
        };
        
        // 创建新路径
        const newPath = new Path2D();
        if (copiedLine.points.length > 0) {
          newPath.moveTo(copiedLine.points[0].x, copiedLine.points[0].y);
          for (let i = 1; i < copiedLine.points.length; i++) {
            newPath.lineTo(copiedLine.points[i].x, copiedLine.points[i].y);
          }
        }
        copiedLine.path = newPath;
        
        copiedLines.push(copiedLine);
      }
    });
    
    return copiedLines;
  }

  // 绘制选中元素的边框
  public drawSelectedBorders(lines: LineData[], texts: TextData[], cards: CardData[], images: ImageData[] = []): void {
    if (!this.ctx) return;
    
    const ctx = this.ctx;
    ctx.save();
    
    // 获取当前缩放，调整线宽和不透明度
    const currentScale = this.viewportService ? this.viewportService.getScale() : 1.0;
    // 统一使用与drawSelectedContentBounds相同的线宽计算公式
    const lineWidth = 1.5 / currentScale;
    const opacityFactor = Math.min(1.0, 1.2 - (currentScale * 0.2)); // 缩放小时增加不透明度
    
    // 定义绘制四角小圆的函数
    const drawCornerCircles = (bounds: Bounds) => {
      // 控制点大小，缩放时调整
      const cornerRadius = 6 / currentScale; // 增加到6像素，以便更明显地显示在图片上
      
      // 四个角的位置和标识
      const corners = [
        { x: bounds.minX, y: bounds.minY, position: 'TOP_LEFT' as const }, // 左上角
        { x: bounds.maxX, y: bounds.minY, position: 'TOP_RIGHT' as const }, // 右上角
        { x: bounds.minX, y: bounds.maxY, position: 'BOTTOM_LEFT' as const }, // 左下角
        { x: bounds.maxX, y: bounds.maxY, position: 'BOTTOM_RIGHT' as const }  // 右下角
      ];
      
      // 获取当前边框颜色
      const currentStroke = ctx.strokeStyle.toString();
      
      // 绘制四个角的小圆
      corners.forEach(corner => {
        // 绘制主圆
        ctx.beginPath();
        ctx.arc(corner.x, corner.y, cornerRadius, 0, Math.PI * 2);
        
        // 判断是否为激活的控制点，如果是则使用实心填充
        const isActive = this.activeControlPoint.position === corner.position;
        
        // 设置填充颜色
        ctx.fillStyle = isActive ? currentStroke : 'white'; // 激活时使用边框颜色填充，否则使用白色
        
        // 设置线宽并绘制
        ctx.lineWidth = lineWidth * 1.5;
        ctx.fill();
        ctx.stroke();
      });
    };
    
    // 获取所有选中组合的ID
    const selectedGroupIds = this.getSelectedGroupIds();
    
    // 如果有选中的组合，绘制每个组合的边框
    if (selectedGroupIds.length > 0) {
      // 遍历所有选中的组合
      for (const groupId of selectedGroupIds) {
        // 如果没有groupService，则跳过
        if (!this.groupService) continue;
        
        // 获取组合中的元素
        const groupElements = this.groupService.getGroupElements(groupId);
        
        // 初始化组合边界框
        let groupBounds: Bounds = {
          minX: Number.MAX_VALUE,
          minY: Number.MAX_VALUE,
          maxX: -Number.MAX_VALUE,
          maxY: -Number.MAX_VALUE
        };
        
        // 检查组合是否包含图片
        let containsImage = false;
        
        // 计算组合边界框
        for (const element of groupElements) {
          if (element.type === 'line' && element.index < lines.length) {
            const lineBounds = this.getLineBoundingBox(lines[element.index], false);
            groupBounds.minX = Math.min(groupBounds.minX, lineBounds.minX);
            groupBounds.minY = Math.min(groupBounds.minY, lineBounds.minY);
            groupBounds.maxX = Math.max(groupBounds.maxX, lineBounds.maxX);
            groupBounds.maxY = Math.max(groupBounds.maxY, lineBounds.maxY);
          } else if (element.type === 'text' && element.index < texts.length) {
            const textBounds = this.getTextBoundingBox(texts[element.index], false);
            groupBounds.minX = Math.min(groupBounds.minX, textBounds.minX);
            groupBounds.minY = Math.min(groupBounds.minY, textBounds.minY);
            groupBounds.maxX = Math.max(groupBounds.maxX, textBounds.maxX);
            groupBounds.maxY = Math.max(groupBounds.maxY, textBounds.maxY);
          } else if (element.type === 'card' && element.index < cards.length) {
            const cardBounds = this.getCardBoundingBox(cards[element.index], false);
            groupBounds.minX = Math.min(groupBounds.minX, cardBounds.minX);
            groupBounds.minY = Math.min(groupBounds.minY, cardBounds.minY);
            groupBounds.maxX = Math.max(groupBounds.maxX, cardBounds.maxX);
            groupBounds.maxY = Math.max(groupBounds.maxY, cardBounds.maxY);
          } else if (element.type === 'image' && element.index < images.length) {
            // 标记该组合包含图片
            containsImage = true;
            const imageBounds = this.getImageBoundingBox(images[element.index], false);
            groupBounds.minX = Math.min(groupBounds.minX, imageBounds.minX);
            groupBounds.minY = Math.min(groupBounds.minY, imageBounds.minY);
            groupBounds.maxX = Math.max(groupBounds.maxX, imageBounds.maxX);
            groupBounds.maxY = Math.max(groupBounds.maxY, imageBounds.maxY);
          }
        }
        
        // 添加边距
        const padding = 5 / currentScale;
        
        // 使用蓝色边框，固定使用和多选时一样的颜色
        ctx.strokeStyle = 'rgba(0, 120, 215, 0.8)';
        ctx.lineWidth = lineWidth;
        
        // 绘制组合边框
        ctx.strokeRect(
          groupBounds.minX - padding,
          groupBounds.minY - padding,
          groupBounds.maxX - groupBounds.minX + (padding * 2),
          groupBounds.maxY - groupBounds.minY + (padding * 2)
        );
        
        // 只有当组合不包含图片时才绘制控制点
        if (!containsImage) {
          // 绘制四角小圆
          const cornerBounds = {
            minX: groupBounds.minX - padding,
            minY: groupBounds.minY - padding,
            maxX: groupBounds.maxX + padding,
            maxY: groupBounds.maxY + padding
          };
          drawCornerCircles(cornerBounds);
        }
      }
    }
    
    // 绘制不在任何组合中的单个元素的边框
    // 绘制选中线条的边框
    this.selectedIndices.forEach(index => {
      if (index < lines.length) {
        // 检查该线条是否属于任何选中的组合
        let isInSelectedGroup = false;
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('line', index);
          // 只要元素属于任何被选中的组合，就不显示元素的灰色边框
          isInSelectedGroup = groupId !== '' && selectedGroupIds.includes(groupId);
        }
        
        // 如果不在任何选中的组合中，或者是用shift单独选择的组合内元素，绘制边框
        if (!isInSelectedGroup || this.selectedGroupId === null) {
          const line = lines[index];
          const lineBounds = this.getLineBoundingBox(line);
          
          // 使用深灰色实线绘制边框，使用一致的线宽
          ctx.strokeStyle = `rgba(60, 60, 60, ${Math.min(0.9, Math.max(0.6, opacityFactor))})`;
          ctx.lineWidth = lineWidth;
          ctx.strokeRect(
            lineBounds.minX,
            lineBounds.minY,
            lineBounds.maxX - lineBounds.minX,
            lineBounds.maxY - lineBounds.minY
          );
          
          // 绘制四角小圆
          drawCornerCircles(lineBounds);
        }
      }
    });
    
    // 绘制选中文本的边框
    this.selectedTextIndices.forEach(index => {
      if (index < texts.length) {
        // 检查该文本是否属于任何选中的组合
        let isInSelectedGroup = false;
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('text', index);
          // 只要元素属于任何被选中的组合，就不显示元素的灰色边框
          isInSelectedGroup = groupId !== '' && selectedGroupIds.includes(groupId);
        }
        
        // 如果不在任何选中的组合中，或者是用shift单独选择的组合内元素，绘制边框
        if (!isInSelectedGroup || this.selectedGroupId === null) {
          const text = texts[index];
          const textBounds = this.getTextBoundingBox(text);
          
          // 绘制单个元素的边框，使用一致的线宽
          ctx.strokeStyle = `rgba(60, 60, 60, ${Math.min(0.9, Math.max(0.6, opacityFactor))})`;
          ctx.lineWidth = lineWidth;
          ctx.strokeRect(
            textBounds.minX,
            textBounds.minY,
            textBounds.maxX - textBounds.minX,
            textBounds.maxY - textBounds.minY
          );
          
          // 绘制四角小圆
          drawCornerCircles(textBounds);
        }
      }
    });
    
    // 绘制选中卡片的边界
    this.selectedCardIndices.forEach(index => {
      if (index >= 0 && index < cards.length) {
        // 检查该卡片是否属于任何选中的组合
        let isInSelectedGroup = false;
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('card', index);
          // 只要元素属于任何被选中的组合，就不显示元素的灰色边框
          isInSelectedGroup = groupId !== '' && selectedGroupIds.includes(groupId);
        }
        
        // 如果不在任何选中的组合中，或者是用shift单独选择的组合内元素，绘制边框
        if (!isInSelectedGroup || this.selectedGroupId === null) {
          const card = cards[index];
          const cardBounds = this.getCardBoundingBox(card);
          
          // 使用深灰色边框，使用一致的线宽
          ctx.strokeStyle = `rgba(60, 60, 60, ${Math.min(0.9, Math.max(0.6, opacityFactor))})`;
          ctx.lineWidth = lineWidth;
          ctx.strokeRect(
            cardBounds.minX,
            cardBounds.minY,
            cardBounds.maxX - cardBounds.minX,
            cardBounds.maxY - cardBounds.minY
          );
          
          // 绘制四角小圆
          drawCornerCircles(cardBounds);
        }
      }
    });
    
    // 绘制选中图片的边界
    this.selectedImageIndices.forEach(index => {
      if (index >= 0 && index < images.length) {
        // 检查该图片是否属于任何选中的组合
        let isInSelectedGroup = false;
        if (this.groupService) {
          const groupId = this.groupService.isElementInAnyGroup('image', index);
          // 只要元素属于任何被选中的组合，就不显示元素的灰色边框
          isInSelectedGroup = groupId !== '' && selectedGroupIds.includes(groupId);
        }
        
        // 如果不在任何选中的组合中，或者是用shift单独选择的组合内元素，绘制边框
        if (!isInSelectedGroup || this.selectedGroupId === null) {
          const image = images[index];
          // 确保使用无边距的边界，无论includeMargin参数如何
          const imageBounds = this.getImageBoundingBox(image, false);
          
          // 使用深灰色边框，使用一致的线宽
          ctx.strokeStyle = `rgba(60, 60, 60, ${Math.min(0.9, Math.max(0.6, opacityFactor))})`;
          ctx.lineWidth = lineWidth;
          ctx.strokeRect(
            imageBounds.minX,
            imageBounds.minY,
            imageBounds.maxX - imageBounds.minX,
            imageBounds.maxY - imageBounds.minY
          );
          
          // 绘制四角小圆 - 直接使用图片的精确边界
          drawCornerCircles(imageBounds);
        }
      }
    });
    
    // 恢复绘图上下文
    this.ctx.restore();
  }

  // 设置视口服务
  public setViewportService(viewportService: ViewportService): void {
    this.viewportService = viewportService;
  }

  // 判断卡片是否在选择区域内
  public isCardInSelectionBounds(card: CardData, selectionBounds: Bounds): boolean {
    // 获取卡片的边界框
    const cardBounds = this.getCardBoundingBox(card);
    
    // 检查两个边界框是否相交
    return this.boundsIntersect(cardBounds, selectionBounds);
  }

  // 添加一个新的辅助方法计算文本的边框
  private getTextBoundingBox(text: TextData, includeMargin: boolean = true): Bounds {
    if (!this.ctx) {
      // 如果没有上下文，返回一个简单的估计边界
      return {
        minX: text.baseX - 10,
        minY: text.baseY - 10,
        maxX: text.baseX + 10,
        maxY: text.baseY + 10
      };
    }
    
    // 计算文本边界 - 考虑缩放
    const creationScale = text.creationScale || 1;
    
    // 估算文本宽度和高度
    let textWidth = 0;
    let textHeight = 0;
    
    // 设置字体大小并考虑创建比例的影响
    const scaledFontSize = text.fontSize * (1 / creationScale);
    this.ctx.font = `${scaledFontSize}px Arial`;
    const lines = text.text.split('\n');
    
    // 找出最宽的行
    for (const line of lines) {
      const metrics = this.ctx.measureText(line);
      textWidth = Math.max(textWidth, metrics.width);
    }
    
    // 计算总高度 - 使用应用了创建比例的行高
    const lineHeight = scaledFontSize * 1.2;
    textHeight = lines.length * lineHeight - (lines.length > 1 ? lineHeight * 0.2 : 0);
    
    // 计算整个文本块的中心点
    const halfWidth = textWidth / 2;
    
    // 对象的基础边界 - 从第一行文字的中心点开始计算
    let bounds = {
      minX: text.baseX - halfWidth,
      minY: text.baseY - (scaledFontSize / 2), // 从第一行文字的中心点向上偏移半个字体大小
      maxX: text.baseX + halfWidth,
      maxY: text.baseY + textHeight - (scaledFontSize / 2) // 向下延伸整个文本高度，减去半个字体大小
    };
    
    // 如果需要包含边距
    if (includeMargin) {
      // 获取当前缩放比例，用于调整边距
      const currentScale = this.viewportService ? this.viewportService.getScale() : 1.0;
      const margin = 5 / currentScale; // 5像素的边距，随缩放调整
      
      bounds.minX -= margin;
      bounds.minY -= margin;
      bounds.maxX += margin;
      bounds.maxY += margin;
    }
    
    // 应用额外的偏移量
    if (text.offsetX || text.offsetY) {
      const offsetX = (text.offsetX || 0) / creationScale;
      const offsetY = (text.offsetY || 0) / creationScale;
      
      bounds.minX += offsetX;
      bounds.minY += offsetY;
      bounds.maxX += offsetX;
      bounds.maxY += offsetY;
    }
    
    return bounds;
  }

  // 添加一个新的辅助方法计算线条的边框
  public getLineBoundingBox(line: LineData, includeMargin: boolean = true): Bounds {
    // 初始化为最大可能值
    let bounds = {
      minX: Infinity,
      minY: Infinity,
      maxX: -Infinity,
      maxY: -Infinity
    };
    
    // 如果线条没有点，返回零边界
    if (!line.points || line.points.length === 0) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    }
    
    // 计算所有点的边界
    line.points.forEach(point => {
      bounds.minX = Math.min(bounds.minX, point.x);
      bounds.minY = Math.min(bounds.minY, point.y);
      bounds.maxX = Math.max(bounds.maxX, point.x);
      bounds.maxY = Math.max(bounds.maxY, point.y);
    });
    
    // 始终为线条添加基本边距，与其他元素（文本、卡片、图片）保持一致
    // 考虑线条粗细
    const lineThickness = line.size || 1;
    
    // 添加边距，包括线条粗细
    bounds.minX -= lineThickness;
    bounds.minY -= lineThickness;
    bounds.maxX += lineThickness;
    bounds.maxY += lineThickness;
    
    // 如果需要包含边距用于UI显示
    if (includeMargin) {
      // 再添加一些额外边距用于用户界面
      const currentScale = this.viewportService ? this.viewportService.getScale() : 1.0;
      const margin = 5 / currentScale;
      
      bounds.minX -= margin;
      bounds.minY -= margin;
      bounds.maxX += margin;
      bounds.maxY += margin;
    }
    
    return bounds;
  }

  // 添加一个新的辅助方法计算卡片的边框
  private getCardBoundingBox(card: CardData, includeMargin: boolean = true): Bounds {
    // 基础边界就是卡片本身
    let bounds = {
      minX: card.x,
      minY: card.y,
      maxX: card.x + card.width,
      maxY: card.y + card.height
    };
    
    // 如果需要包含边距
    if (includeMargin) {
      const currentScale = this.viewportService ? this.viewportService.getScale() : 1.0;
      const margin = 5 / currentScale;
      
      bounds.minX -= margin;
      bounds.minY -= margin;
      bounds.maxX += margin;
      bounds.maxY += margin;
    }
    
    return bounds;
  }

  // 判断两个边界框是否相交
  private boundsIntersect(bounds1: Bounds, bounds2: Bounds): boolean {
    // 如果一个边界框在另一个边界框的左侧、右侧、上方或下方，则它们不相交
    const notIntersect = 
      bounds1.maxX < bounds2.minX || // bounds1在bounds2左侧
      bounds1.minX > bounds2.maxX || // bounds1在bounds2右侧
      bounds1.maxY < bounds2.minY || // bounds1在bounds2上方
      bounds1.minY > bounds2.maxY;   // bounds1在bounds2下方
    
    // 返回相交状态
    return !notIntersect;
  }

  // 判断点是否在边界框内
  private pointInBounds(point: Point, bounds: Bounds): boolean {
    return (
      point.x >= bounds.minX &&
      point.x <= bounds.maxX &&
      point.y >= bounds.minY &&
      point.y <= bounds.maxY
    );
  }

  // 全选功能：选中所有线条、文本和卡片
  public selectAll(lines: LineData[], texts: TextData[], cards: CardData[], images: ImageData[] = []): void {
    // 清除当前选择
    this.clearSelection();
    
    // 选中所有线条
    const lineIndices: number[] = [];
    for (let i = 0; i < lines.length; i++) {
      lineIndices.push(i);
    }
    this.setSelectedIndices(lineIndices);
    
    // 选中所有文本
    const textIndices: number[] = [];
    for (let i = 0; i < texts.length; i++) {
      textIndices.push(i);
    }
    this.setSelectedTextIndices(textIndices);
    
    // 选中所有卡片
    const cardIndices: number[] = [];
    for (let i = 0; i < cards.length; i++) {
      cardIndices.push(i);
    }
    this.setSelectedCardIndices(cardIndices);
    
    // 选中所有图片
    const imageIndices: number[] = [];
    for (let i = 0; i < images.length; i++) {
      imageIndices.push(i);
    }
    this.setSelectedImageIndices(imageIndices);
    
    // 触发重绘
    if (this.ctx && this.ctx.canvas) {
      const event = new CustomEvent('render-required');
      document.dispatchEvent(event);
    }
  }

  // 选择特定元素，如果它属于组，则选择整个组
  public selectElement(type: 'line' | 'text' | 'card' | 'image', index: number, addToExisting: boolean = false): void {
    // 检查元素是否属于某个组
    if (this.groupService) {
      const groupId = this.groupService.isElementInAnyGroup(type, index);
      if (groupId) {
        // 元素属于一个组
        // 检查是否已选中该组且是shift点击（取消选择）
        const isGroupAlreadySelected = this.selectedGroupId === groupId;
        
        if (addToExisting) {
          // 如果按住shift键，只选择单个元素，不选择整个组
          this.clearSelection();
          
          // 根据元素类型添加到相应的选中列表
          if (type === 'line') {
            this.selectedIndices = [index];
          } else if (type === 'text') {
            this.selectedTextIndices = [index];
          } else if (type === 'card') {
            this.selectedCardIndices = [index];
          } else if (type === 'image') {
            this.selectedImageIndices = [index];
          }
          
          // 不设置selectedGroupId，表示没有选中整个组
          this.selectedGroupId = null;
          
          // 重置选中边界缓存
          this.selectedBounds = null;
        } else {
          // 其他情况下（没有按shift键），选择整个组
          this.selectGroup(groupId);
        }
        return;
      }
    }
    
    // 如果不是添加到现有选择，则清除当前选择
    if (!addToExisting) {
      this.clearSelection();
    }
    
    // 元素不属于组，正常选择
    if (type === 'line') {
      if (addToExisting) {
        // 检查元素是否已经被选中
        const index_pos = this.selectedIndices.indexOf(index);
        if (index_pos !== -1) {
          // 如果已经选中，则取消选中
          this.selectedIndices.splice(index_pos, 1);
        } else {
          // 如果未选中，则添加到选中列表
          this.selectedIndices.push(index);
        }
      } else {
        this.selectedIndices = [index];
      }
    } else if (type === 'text') {
      if (addToExisting) {
        // 检查元素是否已经被选中
        const index_pos = this.selectedTextIndices.indexOf(index);
        if (index_pos !== -1) {
          // 如果已经选中，则取消选中
          this.selectedTextIndices.splice(index_pos, 1);
        } else {
          // 如果未选中，则添加到选中列表
          this.selectedTextIndices.push(index);
        }
      } else {
        this.selectedTextIndices = [index];
      }
    } else if (type === 'card') {
      if (addToExisting) {
        // 检查元素是否已经被选中
        const index_pos = this.selectedCardIndices.indexOf(index);
        if (index_pos !== -1) {
          // 如果已经选中，则取消选中
          this.selectedCardIndices.splice(index_pos, 1);
        } else {
          // 如果未选中，则添加到选中列表
          this.selectedCardIndices.push(index);
        }
      } else {
        this.selectedCardIndices = [index];
      }
    } else if (type === 'image') {
      if (addToExisting) {
        // 检查元素是否已经被选中
        const index_pos = this.selectedImageIndices.indexOf(index);
        if (index_pos !== -1) {
          // 如果已经选中，则取消选中
          this.selectedImageIndices.splice(index_pos, 1);
        } else {
          // 如果未选中，则添加到选中列表
          this.selectedImageIndices.push(index);
        }
      } else {
        this.selectedImageIndices = [index];
      }
    }
    
    // 重置选中边界，下次绘制时会重新计算
    this.selectedBounds = null;
  }
  
  // 添加组中的元素到当前选择
  private addGroupElementsToSelection(groupId: string): void {
    if (!this.groupService) return;
    
    // 设置选中的组ID
    this.selectedGroupId = groupId;
    
    // 获取组中的元素
    const elements = this.groupService.getGroupElements(groupId);
    
    // 添加组中的所有元素到当前选择
    for (const element of elements) {
      if (element.type === 'line' && !this.selectedIndices.includes(element.index)) {
        this.selectedIndices.push(element.index);
      } else if (element.type === 'text' && !this.selectedTextIndices.includes(element.index)) {
        this.selectedTextIndices.push(element.index);
      } else if (element.type === 'card' && !this.selectedCardIndices.includes(element.index)) {
        this.selectedCardIndices.push(element.index);
      } else if (element.type === 'image' && !this.selectedImageIndices.includes(element.index)) {
        this.selectedImageIndices.push(element.index);
      }
    }
    
    // 重置选中边界，下次绘制时会重新计算
    this.selectedBounds = null;
  }
  
  // 选择组中的所有元素
  public selectGroup(groupId: string): void {
    if (!this.groupService) return;
    
    // 清除当前选择
    this.clearSelection();
    
    // 设置选中的组ID
    this.selectedGroupId = groupId;
    
    // 获取组中的元素
    const elements = this.groupService.getGroupElements(groupId);
    
    // 选择组中的所有元素
    for (const element of elements) {
      if (element.type === 'line') {
        this.selectedIndices.push(element.index);
      } else if (element.type === 'text') {
        this.selectedTextIndices.push(element.index);
      } else if (element.type === 'card') {
        this.selectedCardIndices.push(element.index);
      } else if (element.type === 'image') {
        this.selectedImageIndices.push(element.index);
      }
    }
    
    // 重置选中边界，下次绘制时会重新计算
    this.selectedBounds = null;
  }
  
  // 判断当前选择是否形成了一个有效的组合
  public canCreateGroup(): boolean {
    const totalSelected = this.selectedIndices.length + 
                         this.selectedTextIndices.length + 
                         this.selectedCardIndices.length +
                         this.selectedImageIndices.length;
    return totalSelected >= 2;
  }
  
  // 获取当前选中元素作为组合元素数组
  public getSelectedElementsAsGroupElements(): GroupElement[] {
    const elements: GroupElement[] = [];
    
    // 添加选中的线条
    this.selectedIndices.forEach(index => {
      elements.push({ type: 'line', index });
    });
    
    // 添加选中的文本
    this.selectedTextIndices.forEach(index => {
      elements.push({ type: 'text', index });
    });
    
    // 添加选中的卡片
    this.selectedCardIndices.forEach(index => {
      elements.push({ type: 'card', index });
    });
    
    // 添加选中的图片
    this.selectedImageIndices.forEach(index => {
      elements.push({ type: 'image', index });
    });
    
    return elements;
  }

  // 从选择中移除一个组
  private deselectGroup(groupId: string): void {
    if (!this.groupService) return;
    
    // 如果与当前选中的组匹配，清除组ID
    if (this.selectedGroupId === groupId) {
      this.selectedGroupId = null;
    }
    
    // 获取组中的元素
    const elements = this.groupService.getGroupElements(groupId);
    
    // 从选择中移除组中的所有元素
    for (const element of elements) {
      if (element.type === 'line') {
        const index = this.selectedIndices.indexOf(element.index);
        if (index !== -1) {
          this.selectedIndices.splice(index, 1);
        }
      } else if (element.type === 'text') {
        const index = this.selectedTextIndices.indexOf(element.index);
        if (index !== -1) {
          this.selectedTextIndices.splice(index, 1);
        }
      } else if (element.type === 'card') {
        const index = this.selectedCardIndices.indexOf(element.index);
        if (index !== -1) {
          this.selectedCardIndices.splice(index, 1);
        }
      } else if (element.type === 'image') {
        const index = this.selectedImageIndices.indexOf(element.index);
        if (index !== -1) {
          this.selectedImageIndices.splice(index, 1);
        }
      }
    }
    
    // 重置选中边界，下次绘制时会重新计算
    this.selectedBounds = null;
  }

  // 获取当前选中元素所属的所有组合ID
  public getSelectedGroupIds(): string[] {
    if (!this.groupService) return [];
    
    const groupIds = new Set<string>();
    
    // 检查选中的线条所属的组
    for (const index of this.selectedIndices) {
      const groupId = this.groupService.isElementInAnyGroup('line', index);
      if (groupId) {
        groupIds.add(groupId);
      }
    }
    
    // 检查选中的文本所属的组
    for (const index of this.selectedTextIndices) {
      const groupId = this.groupService.isElementInAnyGroup('text', index);
      if (groupId) {
        groupIds.add(groupId);
      }
    }
    
    // 检查选中的卡片所属的组
    for (const index of this.selectedCardIndices) {
      const groupId = this.groupService.isElementInAnyGroup('card', index);
      if (groupId) {
        groupIds.add(groupId);
      }
    }
    
    // 检查选中的图片所属的组
    for (const index of this.selectedImageIndices) {
      const groupId = this.groupService.isElementInAnyGroup('image', index);
      if (groupId) {
        groupIds.add(groupId);
      }
    }
    
    return Array.from(groupIds);
  }
  
  // 判断当前选择是否包含多个组合
  public hasMultipleGroupsSelected(): boolean {
    return this.getSelectedGroupIds().length > 1;
  }

  // 添加一个新的辅助方法计算图片的边框
  private getImageBoundingBox(image: ImageData, includeMargin: boolean = true): Bounds {
    // 考虑图片旋转
    if (image.rotation && image.rotation !== 0) {
      // 图片中心点
      const centerX = image.x + image.width / 2;
      const centerY = image.y + image.height / 2;
      
      // 获取图片的四个角点
      const corners = [
        { x: -image.width / 2, y: -image.height / 2 },
        { x: image.width / 2, y: -image.height / 2 },
        { x: image.width / 2, y: image.height / 2 },
        { x: -image.width / 2, y: image.height / 2 }
      ];
      
      // 旋转角点并计算边界
      const rotation = image.rotation;
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      
      // 初始化边界为最大可能值
      let minX = Number.MAX_VALUE;
      let minY = Number.MAX_VALUE;
      let maxX = -Number.MAX_VALUE;
      let maxY = -Number.MAX_VALUE;
      
      // 计算旋转后的边界
      corners.forEach(corner => {
        // 旋转角点
        const rotatedX = corner.x * cos - corner.y * sin;
        const rotatedY = corner.x * sin + corner.y * cos;
        
        // 将角点转换回世界坐标
        const worldX = rotatedX + centerX;
        const worldY = rotatedY + centerY;
        
        // 更新边界
        minX = Math.min(minX, worldX);
        minY = Math.min(minY, worldY);
        maxX = Math.max(maxX, worldX);
        maxY = Math.max(maxY, worldY);
      });
      
      // 创建边界 - 不添加任何边距
      return { minX, minY, maxX, maxY };
    } else {
      // 无旋转，直接使用图片矩形 - 不添加任何边距
      return {
        minX: image.x,
        minY: image.y,
        maxX: image.x + image.width,
        maxY: image.y + image.height
      };
    }
  }

  // 判断图片是否在选择区域内
  public isImageInSelectionBounds(image: ImageData, selectionBounds: Bounds): boolean {
    // 获取图片的边界框 - 使用无边距版本
    const imageBounds = this.getImageBoundingBox(image, false);
    
    // 检查两个边界框是否相交
    return this.boundsIntersect(imageBounds, selectionBounds);
  }

  // isPointInCircle方法保留，因为可能被其他功能使用
  private isPointInCircle(point: Point, centerX: number, centerY: number, radius: number): boolean {
    const dx = point.x - centerX;
    const dy = point.y - centerY;
    return (dx * dx + dy * dy) <= (radius * radius);
  }

  // 设置激活的控制点
  public setActiveControlPoint(position: 'TOP_LEFT' | 'TOP_RIGHT' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT' | 'TOP_CENTER' | 'RIGHT_CENTER' | 'BOTTOM_CENTER' | 'LEFT_CENTER' | null): void {
    this.activeControlPoint.position = position;
  }

  // 获取激活的控制点
  public getActiveControlPoint(): 'TOP_LEFT' | 'TOP_RIGHT' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT' | 'TOP_CENTER' | 'RIGHT_CENTER' | 'BOTTOM_CENTER' | 'LEFT_CENTER' | null {
    return this.activeControlPoint.position;
  }

  // 判断点是否在控制点区域内
  public isPointInControlPoint(point: Point, bounds: Bounds): 'TOP_LEFT' | 'TOP_RIGHT' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT' | 'TOP_CENTER' | 'RIGHT_CENTER' | 'BOTTOM_CENTER' | 'LEFT_CENTER' | null {
    // 获取当前缩放，以调整控制点大小
    const currentScale = this.viewportService ? this.viewportService.getScale() : 1.0;
    // 使用与绘制完全相同的控制点大小，确保检测区域与视觉圆完全一致
    const cornerRadius = 6 / currentScale; // 与drawCornerCircles中使用的大小保持一致
    
    // 边界检测的容差值 - 边界检测不应有容差，应该精确匹配边界
    const borderTolerance = 3 / currentScale;
    
    // 计算边界宽度和高度
    const width = bounds.maxX - bounds.minX;
    const height = bounds.maxY - bounds.minY;
    
    // 使用传入的实际边界进行检测，不做额外修改
    // 检查四个角落的控制点
    const corners = [
      { x: bounds.minX, y: bounds.minY, position: 'TOP_LEFT' as const }, // 左上角
      { x: bounds.maxX, y: bounds.minY, position: 'TOP_RIGHT' as const }, // 右上角
      { x: bounds.minX, y: bounds.maxY, position: 'BOTTOM_LEFT' as const }, // 左下角
      { x: bounds.maxX, y: bounds.maxY, position: 'BOTTOM_RIGHT' as const }  // 右下角
    ];
    
    // 检查点是否在任何控制点的圆内
    for (const corner of corners) {
      const dx = point.x - corner.x;
      const dy = point.y - corner.y;
      const distanceSquared = dx * dx + dy * dy;
      const radiusSquared = cornerRadius * cornerRadius;

      if (distanceSquared <= radiusSquared) {
        return corner.position;
      }
    }
    
    // 检测顶部边界 - 精确匹配边界线
    if (Math.abs(point.y - bounds.minY) <= borderTolerance && 
        point.x >= bounds.minX + cornerRadius * 2 && 
        point.x <= bounds.maxX - cornerRadius * 2) {
      return 'TOP_CENTER';
    }
    
    // 检测底部边界 - 精确匹配边界线
    if (Math.abs(point.y - bounds.maxY) <= borderTolerance && 
        point.x >= bounds.minX + cornerRadius * 2 && 
        point.x <= bounds.maxX - cornerRadius * 2) {
      return 'BOTTOM_CENTER';
    }
    
    // 检测左侧边界 - 精确匹配边界线
    if (Math.abs(point.x - bounds.minX) <= borderTolerance && 
        point.y >= bounds.minY + cornerRadius * 2 && 
        point.y <= bounds.maxY - cornerRadius * 2) {
      return 'LEFT_CENTER';
    }
    
    // 检测右侧边界 - 精确匹配边界线
    if (Math.abs(point.x - bounds.maxX) <= borderTolerance && 
        point.y >= bounds.minY + cornerRadius * 2 && 
        point.y <= bounds.maxY - cornerRadius * 2) {
      return 'RIGHT_CENTER';
    }
    
    return null;
  }
}